import React, { useState, useEffect, useRef } from 'react';

const API_URL = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');

const authHeaders = (token) => ({
  'Content-Type': 'application/json',
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

const PAGE_TYPE_LABELS = {
  content: 'Content Page',
  kc: 'Knowledge Check',
  assessment: 'Assessment',
  certificate: 'Certificate',
  result: 'Result',
};

const PAGE_TYPE_COLORS = {
  content: '#6366f1',
  kc: '#f59e0b',
  assessment: '#94a3b8',
  certificate: '#10b981',
  result: '#94a3b8',
};

export default function TranscriptEditor({ templateId, token, onClose, onAudioReady, getAccessTokenSilently = null }) {
  const [transcripts, setTranscripts] = useState({});
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [audioStatus, setAudioStatus] = useState(null);
  const [error, setError] = useState('');
  const [activePageId, setActivePageId] = useState(null);
  const [voicesLoading, setVoicesLoading] = useState(true);
  const [skipAudioConfirm, setSkipAudioConfirm] = useState(false);
  const pollRef = useRef(null);
  const tokenRef = useRef(token);
  useEffect(() => { tokenRef.current = token; }, [token]);

  const getFreshToken = async () => {
    if (getAccessTokenSilently) {
      try { return await getAccessTokenSilently({ ignoreCache: true }); } catch (_) {}
    }
    return tokenRef.current;
  };

  const apiFetch = async (url, opts = {}) => {
    const tok = tokenRef.current;
    const res = await fetch(url, { ...opts, headers: { ...authHeaders(tok), ...(opts.headers || {}) } });
    if (res.status === 401 && getAccessTokenSilently) {
      const fresh = await getFreshToken();
      return fetch(url, { ...opts, headers: { ...authHeaders(fresh), ...(opts.headers || {}) } });
    }
    return res;
  };

  // Load transcripts + voices on mount
  useEffect(() => {
    const load = async () => {
      const [tRes, vRes, aRes] = await Promise.allSettled([
        apiFetch(`${API_URL}/lesson/templates/${templateId}/transcripts`).then(r => r.json()),
        apiFetch(`${API_URL}/elevenlabs/voices`).then(r => r.json()),
        apiFetch(`${API_URL}/lesson/templates/${templateId}/audio/status`).then(r => r.json()),
      ]);

      if (tRes.status === 'fulfilled' && tRes.value?.success && tRes.value.transcripts) {
        setTranscripts(tRes.value.transcripts);
        const first = Object.keys(tRes.value.transcripts).find(k => tRes.value.transcripts[k]?.transcript !== null);
        if (first) setActivePageId(first);
      } else if (tRes.status === 'rejected' || tRes.value?.error) {
        setError(tRes.value?.error || 'Failed to load transcripts');
      }

      if (vRes.status === 'fulfilled' && vRes.value?.success) {
        setVoices(vRes.value.voices || []);
        if (vRes.value.voices?.length > 0) setSelectedVoice(vRes.value.voices[0].voice_id);
      }

      if (aRes.status === 'fulfilled' && aRes.value?.success) {
        setAudioStatus(aRes.value);
      }

      setLoading(false);
      setVoicesLoading(false);
    };
    load().catch(() => { setError('Failed to load data'); setLoading(false); setVoicesLoading(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  // Poll audio status while generating
  useEffect(() => {
    if (audioStatus?.status === 'generating') {
      pollRef.current = setInterval(async () => {
        try {
          const res = await apiFetch(`${API_URL}/lesson/templates/${templateId}/audio/status`);
          const data = await res.json();
          if (data.success) {
            setAudioStatus(data);
            if (data.status === 'done') {
              clearInterval(pollRef.current);
              setGenerating(false);
              onAudioReady && onAudioReady();
            } else if (data.status === 'error') {
              clearInterval(pollRef.current);
              setGenerating(false);
              setError(data.error || 'Audio generation failed');
            }
          }
        } catch (_) {}
      }, 3000);
    }
    return () => clearInterval(pollRef.current);
  }, [audioStatus?.status, templateId, token, onAudioReady]);

  const editablePages = Object.entries(transcripts).filter(([, v]) => v?.transcript !== null);
  const skippedPages = Object.entries(transcripts).filter(([, v]) => v?.transcript === null);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await apiFetch(`${API_URL}/lesson/templates/${templateId}/transcripts`, {
        method: 'POST',
        body: JSON.stringify({ transcripts }),
      });
      const data = await res.json();
      if (!data.success) setError(data.error || 'Save failed');
    } catch { setError('Save failed'); }
    finally { setSaving(false); }
  };

  const handleGenerate = async () => {
    if (!selectedVoice) { setError('Please select a voice'); return; }
    setError('');
    setGenerating(true);
    try {
      await apiFetch(`${API_URL}/lesson/templates/${templateId}/transcripts`, {
        method: 'POST',
        body: JSON.stringify({ transcripts }),
      });
      const res = await apiFetch(`${API_URL}/lesson/templates/${templateId}/audio/generate`, {
        method: 'POST',
        body: JSON.stringify({ voice_id: selectedVoice }),
      });
      const data = await res.json();
      if (data.success) {
        setAudioStatus({ status: 'generating', progress: 0, total: editablePages.length });
      } else {
        setError(data.error || 'Failed to start generation');
        setGenerating(false);
      }
    } catch { setError('Failed to start generation'); setGenerating(false); }
  };

  const handleSkipAudio = async () => {
    setError('');
    try {
      const res = await apiFetch(`${API_URL}/lesson/templates/${templateId}/review/skip-audio`, {
        method: 'POST',
        body: JSON.stringify({ note: 'Audio skipped by creator' }),
      });
      const data = await res.json();
      if (data.success) onClose && onClose('skipped');
      else setError(data.error || 'Failed to skip');
    } catch { setError('Failed to skip audio'); }
  };

  if (loading) {
    return (
      <div style={overlayStyle}>
        <div style={modalStyle}>
          <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
            Loading transcripts...
          </div>
        </div>
      </div>
    );
  }

  const isGenerating = generating || audioStatus?.status === 'generating';
  const isDone = audioStatus?.status === 'done';
  const progress = audioStatus?.progress || 0;
  const total = audioStatus?.total || editablePages.length;

  return (
    <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onClose && onClose()}>
      <div style={modalStyle}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid rgba(0,255,157,0.1)', background: 'rgba(15,23,42,0.9)' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#f8fafc' }}>🎙️ Read-Aloud Transcripts</h2>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#64748b' }}>
              Review and edit per-page narration, then generate audio with ElevenLabs.
            </p>
          </div>
          <button onClick={() => onClose && onClose()} style={closeBtnStyle}>×</button>
        </div>

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

          {/* Left: page list */}
          <div style={{ width: '200px', minWidth: '200px', borderRight: '1px solid rgba(0,255,157,0.08)', overflowY: 'auto', background: 'rgba(2,6,23,0.8)' }}>
            <div style={{ padding: '10px 12px', fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Pages with audio ({editablePages.length})
            </div>
            {editablePages.map(([pageId, pageData]) => (
              <button
                key={pageId}
                onClick={() => setActivePageId(pageId)}
                style={{
                  width: '100%', textAlign: 'left', padding: '10px 14px',
                  background: activePageId === pageId ? 'rgba(99,102,241,0.15)' : 'transparent',
                  border: 'none', borderLeft: `3px solid ${activePageId === pageId ? PAGE_TYPE_COLORS[pageData.type] || '#6366f1' : 'transparent'}`,
                  cursor: 'pointer', fontSize: '12px', color: activePageId === pageId ? '#f8fafc' : '#94a3b8',
                  fontWeight: activePageId === pageId ? 600 : 400,
                }}
              >
                <div style={{ marginBottom: '2px' }}>{pageId}</div>
                <div style={{ fontSize: '10px', color: PAGE_TYPE_COLORS[pageData.type] || '#6366f1', fontWeight: 600 }}>
                  {PAGE_TYPE_LABELS[pageData.type] || pageData.type}
                </div>
              </button>
            ))}
            {skippedPages.length > 0 && (
              <>
                <div style={{ padding: '10px 12px', fontSize: '10px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '8px' }}>
                  Skipped ({skippedPages.length})
                </div>
                {skippedPages.map(([pageId, pageData]) => (
                  <div key={pageId} style={{ padding: '8px 14px', fontSize: '11px', color: '#475569', borderLeft: '3px solid transparent' }}>
                    {pageId}
                    <div style={{ fontSize: '10px', color: '#334155' }}>{PAGE_TYPE_LABELS[pageData?.type] || pageData?.type}</div>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Right: editor */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* Transcript editor */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              {activePageId && transcripts[activePageId]?.transcript !== null ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '16px', fontWeight: 700, color: '#f8fafc' }}>{activePageId}</span>
                    <span style={{
                      fontSize: '11px', fontWeight: 600, color: '#fff',
                      padding: '2px 8px', borderRadius: '20px',
                      background: PAGE_TYPE_COLORS[transcripts[activePageId]?.type] || '#6366f1',
                    }}>
                      {PAGE_TYPE_LABELS[transcripts[activePageId]?.type] || transcripts[activePageId]?.type}
                    </span>
                    {isDone && audioStatus?.generated?.[activePageId] && (
                      <span style={{ fontSize: '11px', color: '#10b981', fontWeight: 600 }}>✅ Audio ready</span>
                    )}
                  </div>
                  <p style={{ fontSize: '11px', color: '#64748b', marginBottom: '8px' }}>
                    Write natural spoken narration — not a repeat of on-screen text. 3-6 sentences.
                  </p>
                  <textarea
                    value={transcripts[activePageId]?.transcript || ''}
                    onChange={e => setTranscripts(prev => ({
                      ...prev,
                      [activePageId]: { ...prev[activePageId], transcript: e.target.value }
                    }))}
                    style={{
                      width: '100%', minHeight: '160px', padding: '12px',
                      background: 'rgba(15,23,42,0.6)', border: '1.5px solid rgba(0,255,157,0.15)',
                      borderRadius: '8px', color: '#f8fafc', fontSize: '13px',
                      fontFamily: 'inherit', lineHeight: 1.6, resize: 'vertical',
                      outline: 'none', boxSizing: 'border-box',
                    }}
                    placeholder="Write the spoken narration for this page..."
                    disabled={isGenerating || isDone}
                  />
                  <div style={{ fontSize: '11px', color: '#475569', marginTop: '6px' }}>
                    {(transcripts[activePageId]?.transcript || '').length} chars · ~{Math.ceil((transcripts[activePageId]?.transcript || '').split(' ').filter(Boolean).length / 150)} min read
                  </div>
                </>
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: '#475569' }}>
                  {activePageId ? 'This page is skipped (no audio).' : 'Select a page to edit its transcript.'}
                </div>
              )}
            </div>

            {/* Bottom: voice + actions */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(0,255,157,0.08)', background: 'rgba(15,23,42,0.6)', display: 'flex', flexDirection: 'column', gap: '12px' }}>

              {error && (
                <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '6px', padding: '8px 12px', color: '#f87171', fontSize: '12px' }}>
                  {error}
                </div>
              )}

              {/* Progress bar */}
              {isGenerating && (
                <div>
                  <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '6px' }}>
                    Generating audio... {progress}/{total} pages
                  </div>
                  <div style={{ height: '6px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: '3px',
                      background: 'linear-gradient(90deg, #6366f1, #00ff9d)',
                      width: `${total > 0 ? (progress / total) * 100 : 0}%`,
                      transition: 'width 0.3s ease',
                    }} />
                  </div>
                </div>
              )}

              {isDone && (
                <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: '6px', padding: '10px 14px', color: '#34d399', fontSize: '13px', fontWeight: 600 }}>
                  ✅ Audio generated for {progress} pages. Ready for final approval.
                </div>
              )}

              {/* Voice selector */}
              {!isDone && (
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
  <label style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 600, whiteSpace: 'nowrap' }}>Voice:</label>
  <select
    value={selectedVoice}
    onChange={e => setSelectedVoice(e.target.value)}
    disabled={isGenerating || voicesLoading}
    style={{
      flex: 1, padding: '8px 10px', borderRadius: '6px',
      background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(0,255,157,0.15)',
      color: '#f8fafc', fontSize: '12px', outline: 'none',
    }}
  >
    {voicesLoading ? <option>Loading voices...</option> : voices.map(v => (
      <option key={v.voice_id} value={v.voice_id}>{v.name} {v.category ? `· ${v.category}` : ''}</option>
    ))}
  </select>
  <button
    onClick={async () => {
      if (!selectedVoice) return;
      try {
        const res = await apiFetch(`${API_URL}/elevenlabs/voices/${selectedVoice}/preview`);
        const data = await res.json();
        if (data.success && data.preview_url) {
          const a = new Audio(data.preview_url);
          a.play();
        } else {
          setError('No preview available for this voice');
        }
      } catch { setError('Failed to load preview'); }
    }}
    disabled={!selectedVoice || isGenerating}
    style={{
      padding: '8px 12px', borderRadius: '6px', whiteSpace: 'nowrap',
      border: '1px solid rgba(0,255,157,0.2)', background: 'rgba(0,255,157,0.08)',
      color: '#00ff9d', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
    }}
  >
    ▶ Preview
  </button>
</div>
              )}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '8px' }}>
                {!isDone && (
                  <button
                    onClick={handleSave}
                    disabled={saving || isGenerating}
                    style={actionBtnStyle('#6366f1')}
                  >
                    {saving ? 'Saving...' : '💾 Save Transcripts'}
                  </button>
                )}
                {!isDone && (
                  <button
                    onClick={handleGenerate}
                    disabled={isGenerating || !selectedVoice || editablePages.length === 0}
                    style={actionBtnStyle('#00ff9d', '#0f172a')}
                  >
                    {isGenerating ? `Generating ${progress}/${total}...` : '🎙️ Generate Audio'}
                  </button>
                )}
                {isDone && (
                  <button
                    onClick={() => onClose && onClose('done')}
                    style={actionBtnStyle('#10b981')}
                  >
                    ✅ Done — Submit for Final Approval
                  </button>
                )}
                {!isDone && !isGenerating && (
                  <button
                    onClick={() => setSkipAudioConfirm(true)}
                    style={actionBtnStyle('#475569')}
                  >
                    Skip Audio →
                  </button>
                )}
              </div>

              {/* Skip confirm */}
              {skipAudioConfirm && (
                <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', padding: '12px 14px' }}>
                  <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#f87171' }}>
                    Skip audio? The lesson will proceed to final approval without read-aloud.
                  </p>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={handleSkipAudio} style={actionBtnStyle('#ef4444')}>Yes, skip audio</button>
                    <button onClick={() => setSkipAudioConfirm(false)} style={actionBtnStyle('#475569')}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
  zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center',
  backdropFilter: 'blur(4px)',
};

const modalStyle = {
  width: '820px', maxWidth: '95vw', height: '600px', maxHeight: '90vh',
  background: 'rgba(2,6,23,0.97)', border: '1px solid rgba(0,255,157,0.15)',
  borderRadius: '14px', display: 'flex', flexDirection: 'column',
  boxShadow: '0 24px 80px rgba(0,0,0,0.6)', overflow: 'hidden',
};

const closeBtnStyle = {
  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
  color: '#94a3b8', fontSize: '20px', width: '32px', height: '32px',
  borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const actionBtnStyle = (bg, color = '#fff') => ({
  flex: 1, padding: '9px 14px', borderRadius: '7px',
  border: `1px solid ${bg}40`, background: `${bg}20`, color: bg === '#00ff9d' ? color : bg,
  fontWeight: 700, fontSize: '12px', cursor: 'pointer',
});
